import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemIcon,
  ListItemText,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  ListSubheader,
} from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createIdFromLabel, generateRandomId } from '../../../utils/commonUtils';
import { handleValidation } from '../../../utils/formUtils';
import './Custom-Multi-Select.scss';

const useStyles = makeStyles((theme) => ({
  indeterminateColor: {
    color: '#03E67C',
  },
  selectAllText: {
    fontWeight: 400,
  },
  selectedAll: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
    },
  },
}));

const CustomMultiSelect = (props) => {
  const classes = useStyles();
  const {
    item,
    handleInputChange,
    options = [],
    labelField,
    valueField,
    hideNullOption,
    selectAllOption,
    selectAllText = 'Select All',
    nullOptionLabel,
    labelPrefix,
    disabled,
    isSearchable = false,
    getSearchInput,
    searchValue,
    searchPlaceholder,
    isCustomDisplay,
    renderCustomDisplay,
    search,
    showHeaderActions = false,
    clearAllText = 'Clear all',
    showFooterClearInMenu = false,
    renderOptionLabel,
  } = props;
  const [selected, setSelected] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [searchInput, setSearchInput] = useState(null);
  const [searchList, setSearchList] = useState(options);
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
    getContentAnchorEl: null,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'center',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'center',
    },
    variant: 'menu',
    autoFocus: isSearchable ? false : true,
  };

  useEffect(() => {
    const currSelected = item?.value || [];
    setSelected(currSelected);
  }, [item?.value]);

  useEffect(() => {
    if (selectAllOption) {
      setIsAllSelected(options?.length > 0 && selected?.length === options?.length);
    }
  }, [options, selected]);

  const fieldId = item?.fieldName ? createIdFromLabel(item.fieldName) : generateRandomId();

  const handleChange = (event) => {
    let value = event.target.value && event.target.value.filter((v) => v !== undefined);
    if (value[value.length - 1] === 'all') {
      value = selected.length === options.length ? [] : setData(options);
    } else if (value.includes('')) {
      value = [];
    }
    setSelected(value);

    const currField = { ...item };
    currField.value = value;
    handleValidation(currField);
    handleInputChange(currField);
  };

  const setSelectedAndPropagate = (valueArr) => {
    const clean = valueArr?.filter((v) => v !== undefined);
    setSelected(clean);
    const currField = { ...item, value: clean };
    handleValidation(currField);
    handleInputChange(currField);
  };
  const visibleList = useMemo(
    () => (!search ? options : searchList),
    [search, options, searchList],
  );

  const handleClearAll = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setSelectedAndPropagate([]);
  };

  const getErrorMessage = () => {
    const errorMsgs = [];
    item?.validations?.forEach((validation) => {
      if (item.errors[validation]) {
        errorMsgs.push(item.errors[validation]);
      }
    });
    return errorMsgs.length > 0 ? errorMsgs[0] : null;
  };

  const searchTextInput = (searchinput) => {
    return getSearchInput(searchinput);
  };

  const searchResult = (searchinput) => {
    setSearchInput(searchinput);
    if (searchinput === '') setSearchList(options);
    const filterList = options?.filter((item) =>
      item[valueField]?.toLowerCase()?.includes(searchinput.toLowerCase()),
    );
    return setSearchList(filterList);
  };

  const setData = (items) => {
    const item = items?.map((x) => x[valueField || 'id']);
    return item;
  };

  return (
    <FormControl
      className='custom-multi-select w-100'
      required={item?.validations && item?.validations?.includes('required')}
      error={getErrorMessage() ? true : false}
    >
      <InputLabel id={fieldId + '_label'}>
        {labelPrefix} {item?.fieldName}
      </InputLabel>
      <Select
        disabled={disabled}
        labelId={fieldId + '_label'}
        id={fieldId + '_select'}
        multiple
        value={selected}
        onChange={handleChange}
        onClose={() => {
          if (!search) {
            props?.getSearchInput?.('');
          } else {
            setSearchInput('');
            setSearchList(options);
          }
        }}
        renderValue={(selected) =>
          isCustomDisplay ? renderCustomDisplay(selected) : selected?.join(', ')
        }
        MenuProps={MenuProps}
      >
        {isSearchable && (
          <ListSubheader className='custom-search-text'>
            <TextField
              value={!search ? searchValue : searchInput}
              autoFocus
              fullWidth
              placeholder={searchPlaceholder}
              onChange={(e) =>
                !search ? searchTextInput(e?.target?.value) : searchResult(e?.target?.value)
              }
              className='custom-search-input'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
            />
          </ListSubheader>
        )}

        {!hideNullOption && (
          <MenuItem value=''>
            <em>{nullOptionLabel || 'None'}</em>
          </MenuItem>
        )}

        {selectAllOption && (
          <MenuItem
            value='all'
            classes={{
              root: isAllSelected
                ? 'custom-multi-select-list ' + classes.selectedAll
                : 'custom-multi-select-list',
            }}
          >
            <ListItemIcon>
              <Checkbox
                color='primary'
                classes={{ indeterminate: classes.indeterminateColor }}
                checked={isAllSelected || false}
                indeterminate={selected?.length > 0 && selected?.length < options?.length}
              />
            </ListItemIcon>
            <ListItemText classes={{ primary: classes.selectAllText }} primary={selectAllText} />
          </MenuItem>
        )}

        {visibleList?.map((item, key) => {
          return typeof item === 'object' ? (
            <MenuItem
              className='custom-multi-select-list'
              key={key}
              value={item[valueField || 'id']}
            >
              <ListItemIcon>
                <Checkbox color='primary' checked={selected?.indexOf(item[valueField]) > -1} />
              </ListItemIcon>
              {renderOptionLabel ? renderOptionLabel(item) : <ListItemText primary={item[labelField || 'name']} />}
            </MenuItem>
          ) : (
            <MenuItem className='custom-multi-select-list' key={key} value={item}>
              <ListItemIcon>
                <Checkbox color='primary' checked={selected?.indexOf(item) > -1} />
              </ListItemIcon>
              <ListItemText primary={item} />
            </MenuItem>
          );
        })}
        {( (isSearchable && showHeaderActions) || showFooterClearInMenu ) && visibleList?.length > 0 && (
          <>
            <li className='custom-actions-spacer' />
            <ListSubheader className='custom-actions-footer'>
              <span
                className='action-right'
                onMouseDown={handleClearAll}
                role='button'
                tabIndex={-1}
              >
                {clearAllText}
              </span>
            </ListSubheader>
          </>
        )}
      </Select>
      <FormHelperText>{getErrorMessage()}</FormHelperText>
    </FormControl>
  );
};

export default CustomMultiSelect;
